import React, { useState, useEffect, Suspense, lazy } from 'react';
import axios from 'axios';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import GlobalBackfillIndicator from '@/components/GlobalBackfillIndicator';
import DashboardLoadProgress from '@/components/DashboardLoadProgress';
import { AppProvider, useApp, ROLE_LABELS } from '@/context/AppContext';
import { Sidebar } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import { PushGate } from '@/components/layout/PushGate';
import { BranchUnmappedBanner } from '@/components/layout/BranchUnmappedBanner';
import { DailyReverifyGate } from '@/components/layout/DailyReverifyGate';
import { ExcelPasswordDialog } from '@/components/layout/ExcelPasswordDialog';
import { MobileTabBar } from '@/components/layout/MobileTabBar';
import { MobilePrimaryFab } from '@/components/layout/MobilePrimaryFab';
import ScrollHint from '@/components/layout/ScrollHint';
import MobileHome from '@/components/layout/MobileHome';
import { BusinessFilter } from '@/components/layout/BusinessFilter';
import ErrorBoundary from '@/components/ErrorBoundary';

// Login is the entry-point — keep it eager so first-paint doesn't wait
// for an extra chunk to download.
import LoginPage from '@/pages/LoginPage';

// Every other route is `React.lazy` so the initial bundle stays small
// (login painted from ~150 KB instead of ~670 KB). The pages download
// in parallel once the user is authenticated and the router actually
// resolves a protected route.
// Dashboard removed
const TeamManagement = lazy(() => import('@/pages/TeamManagement'));
const HitchNotifications = lazy(() => import('@/pages/HitchNotifications'));
const EmailNotifications = lazy(() => import('@/pages/EmailNotifications'));
const HitchZoneAudit = lazy(() => import('@/pages/HitchZoneAudit'));
const Tally = lazy(() => import('@/pages/Tally'));
const OnboardIdList = lazy(() => import('@/pages/OnboardIdList'));
const BranchesPage = lazy(() => import('@/pages/Branches'));
const VehicleInventory = lazy(() => import('@/pages/VehicleInventory'));
const NcmcInventory = lazy(() => import('@/pages/NcmcInventory'));
const UsersCreated = lazy(() => import('@/pages/UsersCreated'));
const VehicleBookings = lazy(() => import('@/pages/VehicleBookings'));
const Payments = lazy(() => import('@/pages/Payments'));
const VehicleModelMaster = lazy(() => import('@/pages/VehicleModelMaster'));
const VehiclePricing = lazy(() => import('@/pages/VehiclePricing'));
const CreateQuote = lazy(() => import('@/pages/CreateQuote'));
const BillingModule = lazy(() => import('@/pages/BillingModule'));
const VehicleAddition = lazy(() => import('@/pages/VehicleAddition'));
const Visits = lazy(() => import('@/pages/Visits'));
const BusinessPlaces = lazy(() => import('@/pages/BusinessPlaces'));
const SalesPoints = lazy(() => import('@/pages/SalesPoints'));
const Performance = lazy(() => import('@/pages/Performance'));
const UserTree = lazy(() => import('@/pages/UserTree'));
const Approvals = lazy(() => import('@/pages/Approvals'));
const SheetImport = lazy(() => import('@/pages/SheetImport'));
const LeadDistribution = lazy(() => import('@/pages/LeadDistribution'));
const LeadTracking = lazy(() => import('@/pages/LeadTracking'));
const PerfWatch = lazy(() => import('@/pages/PerfWatch'));
const ApprovalReview = lazy(() => import('@/pages/ApprovalReview'));
const DataImport = lazy(() => import('@/pages/DataImport'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));
const TravelPlan = lazy(() => import('@/pages/TravelPlan'));
const RecentlyDeleted = lazy(() => import('@/pages/RecentlyDeleted'));
const Attendance = lazy(() => import('@/pages/Attendance'));
const FieldActivity = lazy(() => import('@/pages/FieldActivity'));
const TravelApprovals = lazy(() => import('@/pages/TravelApprovals'));
const TravelApprovalReview = lazy(() => import('@/pages/TravelApprovalReview'));
const UnplannedVisits = lazy(() => import('@/pages/UnplannedVisits'));
const Revenue = lazy(() => import('@/pages/Revenue'));
const MarginManagement = lazy(() => import('@/pages/MarginManagement'));
const HRDashboard = lazy(() => import('@/pages/HRDashboard'));
const PayoutManagement = lazy(() => import('@/pages/PayoutManagement'));
const ApiDocs = lazy(() => import('@/pages/ApiDocs'));
const SystemDiagnostics = lazy(() => import('@/pages/SystemDiagnostics'));
const Leads = lazy(() => import('@/pages/Leads'));
const ExpenseClaims = lazy(() => import('@/pages/ExpenseClaims'));
const InventoryTracer = lazy(() => import('@/pages/InventoryTracer'));
const BankingTransactions = lazy(() => import('@/pages/BankingTransactions'));
const HitchZoneCompare = lazy(() => import('@/pages/HitchZoneCompare'));
const VrnTracer = lazy(() => import('@/pages/VrnTracer'));
const PushNotifications = lazy(() => import('@/pages/PushNotifications'));
import StatusUpdatePreview from '@/pages/StatusUpdatePreview';
import TravelDistancePreview from '@/pages/TravelDistancePreview';
import DeptFeatureMappingPreview from '@/pages/DeptFeatureMappingPreview';
import BusinessLineAccessPreview from '@/pages/BusinessLineAccessPreview';
import LeadNotesMappingPreview from '@/pages/LeadNotesMappingPreview';
import TxnHighlighterPreview from '@/pages/TxnHighlighterPreview';
import ZeroOnboardingPreview from '@/pages/ZeroOnboardingPreview';
const MyProfile = lazy(() => import('@/pages/MyProfile'));
const CallTracker = lazy(() => import('@/pages/CallTracker'));
const TaskTracker = lazy(() => import('@/pages/TaskTracker'));
const MobileNumberLookup = lazy(() => import('@/pages/MobileNumberLookup'));
const IdfcTollTxn = lazy(() => import('@/pages/IdfcTollTxn'));
const LivquikTollTxn = lazy(() => import('@/pages/LivquikTollTxn'));
const ThirdPartyApps = lazy(() => import('@/pages/ThirdPartyApps'));
const ChatbotAdmin = lazy(() => import('@/pages/ChatbotAdmin'));
const SupportChat = lazy(() => import('@/pages/SupportChat'));
const PrepaidCardDispatch = lazy(() => import('@/pages/PrepaidCardDispatch'));
const TxnAuditor = lazy(() => import('@/pages/TxnAuditor'));
const HiteamWalletTxns = lazy(() => import('@/pages/HiteamWalletTxns'));
const WalletAudit = lazy(() => import('@/pages/WalletAudit'));
const TaskNotes = lazy(() => import('@/pages/TaskNotes'));
const Reminders = lazy(() => import('@/pages/Reminders'));
const SubUserManager = lazy(() => import('@/pages/SubUserManager'));
import { ProfileDialog, HiteamSetupPrompt, PasswordSetupPrompt } from '@/components/layout/ProfileDialog';
import BulkPushDock from '@/components/admin/BulkPushDock';
import ExternalApiDock from '@/components/admin/ExternalApiDock';
const RewardsMockup = React.lazy(() => import('@/components/admin/RewardsMockup'));
const LeadDistributionMockup = React.lazy(() => import('@/pages/LeadDistributionMockup'));
const ForgotPassword = React.lazy(() => import('@/pages/ForgotPassword'));
import BearerRefreshMonitor from '@/components/admin/BearerRefreshMonitor';
import DeploymentBanner from '@/components/DeploymentBanner';

// Prefetch ALL page chunks in the background after login so switching between
// sidebar items is instant (no per-click chunk download). Chunks are warmed in
// small staggered batches during idle time to avoid a network/CPU spike.
function prefetchPages() {
  const loaders = [
    () => import('@/pages/TeamManagement'), () => import('@/pages/Visits'),
    () => import('@/pages/BusinessPlaces'), () => import('@/pages/SalesPoints'),
    () => import('@/pages/Performance'), () => import('@/pages/UserTree'),
    () => import('@/pages/Approvals'), () => import('@/pages/ApprovalReview'),
    () => import('@/pages/DataImport'), () => import('@/pages/AdminPanel'),
    () => import('@/pages/TravelPlan'), () => import('@/pages/RecentlyDeleted'),
    () => import('@/pages/Attendance'), () => import('@/pages/FieldActivity'),
    () => import('@/pages/TravelApprovals'), () => import('@/pages/TravelApprovalReview'),
    () => import('@/pages/UnplannedVisits'), () => import('@/pages/Revenue'),
    () => import('@/pages/MarginManagement'), () => import('@/pages/HRDashboard'),
    () => import('@/pages/PayoutManagement'), () => import('@/pages/ApiDocs'),
    () => import('@/pages/SystemDiagnostics'), () => import('@/pages/Leads'),
    () => import('@/pages/ExpenseClaims'), () => import('@/pages/InventoryTracer'),
    () => import('@/pages/BankingTransactions'), () => import('@/pages/VrnTracer'),
    () => import('@/pages/PushNotifications'), () => import('@/pages/MyProfile'),
    () => import('@/pages/CallTracker'), () => import('@/pages/TaskTracker'),
    () => import('@/pages/MobileNumberLookup'), () => import('@/pages/IdfcTollTxn'),
    () => import('@/pages/LivquikTollTxn'), () => import('@/pages/ThirdPartyApps'),
    () => import('@/pages/ChatbotAdmin'), () => import('@/pages/SupportChat'),
    () => import('@/pages/PrepaidCardDispatch'), () => import('@/pages/TxnAuditor'),
    () => import('@/pages/HiteamWalletTxns'), () => import('@/pages/WalletAudit'),
    () => import('@/pages/TaskNotes'), () => import('@/pages/Reminders'),
    () => import('@/pages/SubUserManager'), () => import('@/pages/HitchNotifications'),
    () => import('@/pages/EmailNotifications'), () => import('@/pages/HitchZoneAudit'),
    () => import('@/pages/OnboardIdList'), () => import('@/pages/Branches'),
    () => import('@/pages/VehicleInventory'), () => import('@/pages/VehicleBookings'),
    () => import('@/pages/Payments'), () => import('@/pages/VehicleModelMaster'),
    () => import('@/pages/VehiclePricing'), () => import('@/pages/CreateQuote'),
    () => import('@/pages/BillingModule'), () => import('@/pages/VehicleAddition'),
    () => import('@/pages/SheetImport'), () => import('@/pages/LeadDistribution'),
    () => import('@/pages/LeadTracking'), () => import('@/pages/PerfWatch'),
  ];
  let i = 0;
  const BATCH = 3;
  const pump = () => {
    if (i >= loaders.length) return;
    for (let n = 0; n < BATCH && i < loaders.length; n++, i++) {
      try { loaders[i]().catch(() => {}); } catch (_) {}
    }
    // Keep warming during idle; small gap between batches avoids a spike.
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => setTimeout(pump, 150), { timeout: 2000 });
    else setTimeout(pump, 300);
  };
  const start = () => setTimeout(pump, 1500);
  if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 4000 });
  else setTimeout(start, 2000);
}

// Tiny inline fallback shown while a route chunk is downloading.
const RouteFallback = () => (
  <div className="flex items-center justify-center h-[60vh] text-slate-400 text-sm">
    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping mr-2" />
    Loading…
  </div>
);

// Route-level guard for sub-users: redirect to /task-tracker if feature not granted.
// For regular users this is a no-op pass-through.
const PATH_TO_FEATURE = {
  '/team': 'team', '/org-tree': 'org-tree',
  '/mobile-lookup': 'mobile-lookup',
  '/visits': 'visits', '/field-activity': 'field-activity',
  '/travel-plans': 'travel-plans', '/travel-approvals': 'travel-plans',
  '/unplanned-visits': 'unplanned-visits',
  '/attendance': 'attendance',
  '/revenue': 'revenue', '/margin': 'margin', '/payout': 'payout',
  '/performance': 'performance', '/hr-dashboard': 'hr-dashboard',
  '/inventory': 'inventory', '/vrn-tracer': 'vrn-tracer',
  '/banking': 'banking', '/hitchzone-compare': 'banking', '/idfc-toll-txn': 'idfc-toll',
  '/livquik-toll-txn': 'livquik-toll',
  '/prepaid-card-dispatch': 'prepaid-card',
  '/ncmc-inventory': 'prepaid-card',
  '/txn-auditor': 'txn-audit',
  '/hiteam-wallet-txns': 'txn-audit',
  '/wallet-audit': 'txn-audit',
  '/onboard-id-list': 'onboard-id-list',
  '/approvals': 'approvals', '/places': 'places', '/expense-claims': 'expense-claims',
  '/leads': 'leads', '/call-tracker': 'call-tracker',
  '/task-tracker': 'task-tracker', '/notes': 'notes', '/reminders': 'reminders',
  '/push-notifications': 'push-notifications', '/sales-points': 'sales-points',
  '/sheet-import': 'sheet-import',
  '/lead-distribution': 'lead-distribution',
  '/lead-tracking': 'lead-tracking',
  '/perf-watch': 'perf-watch',
  '/admin': 'admin', '/sub-users': null, '/import': 'import',
  '/recently-deleted': null,
  '/diagnostics': 'system-test', '/planner': 'visits',
};

// Business-line-specific routes (mirror the sidebar's businessLineKey groups).
// A regular user must ALSO have a matching assigned business line to reach these.
const PATH_TO_BUSINESS_LINE = {
  '/margin': 'fastag', '/inventory': 'fastag', '/idfc-toll-txn': 'fastag',
  '/livquik-toll-txn': 'fastag',
  '/performance': 'fastag', '/vrn-tracer': 'fastag', '/payout': 'fastag',
  '/prepaid-card-dispatch': 'prepaid_cards',
  '/ncmc-inventory': 'prepaid_cards',
  '/banking': 'banking',
  '/hitchzone-compare': 'banking',
};

function SubUserGuard({ children }) {
  const { currentUser, roleFeatures, departmentFeatures, bootstrapped, businessLines } = useApp();
  const location = useLocation();
  const path = location.pathname;

  const matchedEntry = Object.entries(PATH_TO_FEATURE).find(([p]) =>
    path === p || path.startsWith(p + '/')
  );
  const featureKey = matchedEntry ? matchedEntry[1] : undefined;

  const role = (currentUser?.role || '').toLowerCase();
  const isPrivileged = ['admin', 'sub_admin', 'business_head'].includes(role);
  const dept = (currentUser?.department || '').trim();
  const deptFeatures = departmentFeatures || [];

  // Mirror the sidebar's business-line group visibility.
  const hasBusinessLine = (key) => {
    const userBizIds = currentUser?.assigned_businesses || [];
    const isCoordinator = role.includes('coordinator') || (currentUser?.mapped_manager_ids || []).length > 0;
    // No business line assigned → department grant is enough (e.g. Transaction Monitor / Accounts).
    if (userBizIds.length === 0) return true;
    if (isCoordinator) return true;
    return (businessLines || []).some(bl =>
      userBizIds.includes(bl.id) &&
      ((bl.name || '').toLowerCase().includes(key) ||
       (bl.label || '').toLowerCase().includes(key) ||
       (bl.id || '').toLowerCase().includes(key))
    );
  };

  // Decide whether the current regular user may view this route.
  const computeBlocked = () => {
    if (!currentUser || isPrivileged || !bootstrapped) return false;
    if (currentUser.is_sub_user) {
      return !!matchedEntry && (featureKey === null || !roleFeatures.includes(featureKey));
    }
    const ALWAYS_OK = ['/profile'];
    if (ALWAYS_OK.some(p => path === p || path.startsWith(p + '/'))) return false;
    // HR reaches the Admin Panel purely for the Departments & Designations tabs
    // (the panel itself gates HR down to just those two sub-tabs).
    if (currentUser.role === 'hr' && path === '/admin') return false;
    if (!matchedEntry) return false;
    if (!dept || deptFeatures.length === 0) return true;
    // Shared onboarding page: FASTag (revenue+fastag) OR Prepaid (prepaid-onboarding+prepaid_cards).
    if (path === '/revenue' || path.startsWith('/revenue/')) {
      const okFastag = deptFeatures.includes('revenue') && hasBusinessLine('fastag');
      const okPrepaid = deptFeatures.includes('prepaid-onboarding') && hasBusinessLine('prepaid_cards');
      return !(okFastag || okPrepaid);
    }
    // Shared Performance page: FASTag OR Prepaid, both use the 'performance' feature.
    if (path === '/performance' || path.startsWith('/performance/')) {
      if (!deptFeatures.includes('performance')) return true;
      return !(hasBusinessLine('fastag') || hasBusinessLine('prepaid_cards'));
    }
    if (featureKey === null || !deptFeatures.includes(featureKey)) return true;
    const blKey = PATH_TO_BUSINESS_LINE[path] ||
      Object.entries(PATH_TO_BUSINESS_LINE).find(([p]) => path.startsWith(p + '/'))?.[1];
    if (blKey && !hasBusinessLine(blKey)) return true;
    return false;
  };

  const blocked = computeBlocked();

  // Where to send a blocked regular user (first allowed page, else profile).
  const redirectTarget = () => {
    if (currentUser?.is_sub_user) return '/task-tracker';
    const home = Object.entries(PATH_TO_FEATURE).find(([p, k]) => {
      if (!k || !deptFeatures.includes(k)) return false;
      const blKey = PATH_TO_BUSINESS_LINE[p];
      return !blKey || hasBusinessLine(blKey);
    });
    return home ? home[0] : '/profile';
  };

  useEffect(() => {
    if (blocked && !currentUser?.is_sub_user) {
      toast.error("You don't have access to that page", {
        id: 'route-access-denied',
        description: 'Redirected to a page you can open. Ask your admin if you need access.',
      });
    }
  }, [blocked, path]); // eslint-disable-line react-hooks/exhaustive-deps

  if (blocked) return <Navigate to={redirectTarget()} replace />;
  return children;
}

const pageTitles = {
  '/': 'Task Tracker',
  '/team': 'Team Management',
  '/org-tree': 'Organization Tree',
  '/visits': 'Client Visits',
  '/places': 'Business Places',
  '/approvals': 'Approvals',
  '/sales-points': 'Sales Points',
  '/performance': 'Performance',
  '/planner': 'Visit Planner',
  '/import': 'Data Import',
  '/admin': 'Admin Panel',
  '/travel-plans': 'Travel Plans',
  '/recently-deleted': 'Recently Deleted',
  '/field-activity': 'Field Activity',
  '/travel-approvals': 'Travel Approvals',
  '/unplanned-visits': 'Unplanned Visits',
  '/revenue': 'Customer Onboarding',
  '/margin': 'FASTag Margin Management',
  '/hr-dashboard': 'HR Dashboard',
  '/payout': 'FASTag Payout',
  '/leads': 'Leads',
  '/call-tracker': 'Call Tracker',
  '/task-tracker': 'Task Tracker',
  '/mobile-lookup': 'Mobile Number Lookup',
  '/expense-claims': 'Expense Claims',
  '/inventory': 'Inventory',
  '/banking': 'Hitch Zone Transactions',
  '/hitchzone-compare': 'Hitch Zone Txn Comparison',
  '/vrn-tracer': 'VRN Tracer',
  '/push-notifications': 'HiTeam Push Notifications',
  '/profile': 'My Profile',
  '/docs': 'API Documentation',
  '/onboard-id-list': 'Onboard ID List',
  '/idfc-toll-txn': 'IDFC Toll Transactions',
  '/livquik-toll-txn': 'LivQuik Toll Transactions',
  '/third-party-logins': 'Third-Party Logins',
  '/chatbot-admin': 'ChatBot Hub — Support',
  '/support-chat': 'Support Chat',
  '/prepaid-card-dispatch': 'Prepaid Card Dispatch Report',
  '/ncmc-inventory': 'Prepaid Card Inventory',
  '/attendance': 'Attendance',
  '/lead-distribution': 'Lead Distribution',
  '/lead-tracking': 'Lead Tracking',
  '/perf-watch': 'Watchlist Performance',
  '/notes': 'My Notes',
  '/sheet-import': 'Google Sheet Import',
  '/reminders': 'Reminders',
  '/notifications': 'hitch.services Notifications',
  '/email-notifications': 'Email Notifications',
  '/hitchzone-audit': 'Transaction Highlighter',
  '/tally': 'Tally Ledgers',
  '/txn-auditor': 'Transaction Auditor',
  '/hiteam-wallet-txns': 'HiTeam Wallet Transactions',
  '/wallet-audit': 'HiTeam Wallet Audit',
  '/vehicle-inventory': 'Vehicle Inventory',
  '/vehicle-addition': 'Vehicle Addition',
  '/vehicle-bookings': 'Vehicle Bookings',
  '/payments': 'Customer Payments',
  '/vehicle-model-master': 'Vehicle Model Master',
  '/vehicle-pricing': 'Vehicle Pricing',
  '/create-quote': 'Create Quote',
  '/billing': 'Billing',
  '/sub-users': 'Sub Users',
  '/diagnostics': 'System Test',
  '/dashboard': 'Dashboard',
  '/branches': 'Branches',
};

const AppContent = () => {
  const { currentUser, setCurrentUser, seedData, seeded, api, bootstrapFromToken } = useApp();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authState, setAuthState] = useState('loading'); // loading | authenticated | login | unreachable
  const [authHint, setAuthHint] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [showHiteamSetup, setShowHiteamSetup] = useState(false);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // App mounted & rendered successfully — clear the stale-chunk reload guard
  // so a future post-deploy chunk error can auto-recover with a fresh reload.
  useEffect(() => {
    sessionStorage.removeItem('chunk_reload_attempted');
  }, []);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  // Validate session token from URL on mount — uses single bootstrap call
  useEffect(() => {
    const validateSession = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionToken = params.get('session_token');
      
      // Impersonated tabs arrive via ?session_token=xxx — use sessionStorage (tab-isolated)
      // so the impersonated token never overwrites the admin's localStorage token.
      const storedToken = sessionToken
        ? null  // from URL → will store in sessionStorage below
        : (sessionStorage.getItem('session_token') || localStorage.getItem('session_token'));
      const tokenToValidate = sessionToken || storedToken;

      if (!tokenToValidate) {
        setAuthState('login');
        return;
      }

      // Only an explicit auth rejection (401/403/404) invalidates the token. Server
      // hiccups (502/timeouts during a deploy or restart) must NOT log everyone out:
      // retry with backoff while showing the loader, then offer a manual retry.
      const isAuthReject = (e) => [401, 403, 404].includes(e?.response?.status);
      const sleep = (ms) => new Promise(r => setTimeout(r, ms));
      let lastErr = null;
      for (let attempt = 0; attempt < 8; attempt++) {
        try {
          // Use bootstrap — single call replaces validate-session + 5 follow-up calls
          let user = null;
          try { user = await bootstrapFromToken(tokenToValidate); }
          catch (e) { if (isAuthReject(e)) throw e; }
          if (!user) {
            // Bootstrap unavailable — fallback to legacy validate
            const res = await api.validateSession(tokenToValidate);
            setCurrentUser(res.data);
            if (!res.data.has_password && !localStorage.getItem('password_setup_dismissed')) {
              setTimeout(() => setShowPasswordSetup(true), 800);
            }
          } else if (!user.has_password && !localStorage.getItem('password_setup_dismissed')) {
            setTimeout(() => setShowPasswordSetup(true), 800);
          }
          setAuthState('authenticated');
          prefetchPages();
          // Seed in background after auth (non-blocking)
          if (!seeded) seedData();
          // Store token — impersonated tabs use sessionStorage (tab-isolated, won't clobber admin localStorage)
          if (sessionToken) {
            sessionStorage.setItem('session_token', tokenToValidate);
            window.history.replaceState({}, '', window.location.pathname);
          } else {
            localStorage.setItem('session_token', tokenToValidate);
          }
          return;
        } catch (e) {
          lastErr = e;
          if (isAuthReject(e)) break;
          setAuthHint(`Server is waking up… retrying (${attempt + 1}/8)`);
          await sleep(Math.min(2000 + attempt * 2000, 10000));
        }
      }
      if (isAuthReject(lastErr)) {
        sessionStorage.removeItem('session_token');
        localStorage.removeItem('session_token');
        setAuthState('login');
      } else {
        setAuthState('unreachable');
      }
    };

    if (!currentUser) {
      validateSession();
    } else {
      setAuthState('authenticated');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (sessionToken, user) => {
    localStorage.setItem('session_token', sessionToken);
    if (!seeded) await seedData();
    setCurrentUser(user);
    setAuthState('authenticated');
    prefetchPages();
    if (!user.has_password && !localStorage.getItem('password_setup_dismissed')) {
      setTimeout(() => setShowPasswordSetup(true), 800);
    }
    // Check if user has HiTeam credentials
    try {
      const res = await api.getHiteamCredentials(user.id);
      if (!res.data.has_credentials && !localStorage.getItem('hiteam_setup_dismissed')) {
        setTimeout(() => setShowHiteamSetup(true), user.has_password ? 800 : 1500);
      }
    } catch (e) { /* silently skip */ }
  };

  const isNonAdmin = currentUser && currentUser.role !== 'system_admin';

  const handleLogout = () => {
    localStorage.removeItem('session_token');
    sessionStorage.removeItem('session_token');
    setCurrentUser(null);
    setAuthState('login');
  };

  // Global 401 interceptor: when the backend rejects the session (e.g., role
  // changed → sessions invalidated), auto-logout so the user re-authenticates
  // with fresh role/features.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        // A wrong password on the daily re-verify prompt returns 401 but must
        // NOT log the user out — let the gate show its own error instead.
        // Only a rejected SESSION logs the user out. Many endpoints return 401 for
        // "requester_id required" (a request fired before the user finished loading)
        // or for third-party auth problems — those must never end the session.
        const url = err?.config?.url || '';
        const status = err?.response?.status;
        const detail = String(err?.response?.data?.detail || '');
        const isReverify = url.includes('/auth/reverify-password');
        const sessionRejected = /\/auth\/(validate-session|bootstrap|reverify-status)/.test(url) ||
          /session (expired|not found)|invalid session|permanently deactivated|account has been (removed|permanently)/i.test(detail);
        if ((status === 401 || status === 403) && authState === 'authenticated' && !isReverify && sessionRejected) {
          localStorage.removeItem('session_token');
          sessionStorage.removeItem('session_token');
          setCurrentUser(null);
          setAuthState('login');
          try { if (/permanently deactivated|account has been (removed|permanently)/i.test(detail)) toast.error(detail); } catch (_) {}
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [authState, setCurrentUser]);

  // Session heartbeat: periodically re-validate the session so a user who is
  // deactivated (status → permanently_inactive) or whose sessions were revoked
  // is force-logged-out within ~60s instead of lingering in an open tab. The
  // global interceptor above turns the 401/403 into a logout; this just pings.
  useEffect(() => {
    if (authState !== 'authenticated') return;
    const ping = async () => {
      const token = sessionStorage.getItem('session_token') || localStorage.getItem('session_token');
      if (!token) return;
      try { await api.validateSession(token); } catch (_) { /* interceptor handles logout */ }
    };
    const id = setInterval(ping, 60000);
    return () => clearInterval(id);
  }, [authState]); // eslint-disable-line react-hooks/exhaustive-deps


  // Public preview routes (no auth required)
  if (location.pathname === '/ui-preview/status-update') {
    return <StatusUpdatePreview />;
  }

  if (location.pathname === '/ui-preview/travel-distance') {
    return <TravelDistancePreview />;
  }

  if (location.pathname === '/ui-preview/dept-feature-mapping') {
    return <DeptFeatureMappingPreview />;
  }

  if (location.pathname === '/ui-preview/business-line-access') {
    return <BusinessLineAccessPreview />;
  }

  if (location.pathname === '/ui-preview/lead-notes-mapping') {
    return <LeadNotesMappingPreview />;
  }

  if (location.pathname === '/ui-preview/txn-highlighter') {
    return <TxnHighlighterPreview />;
  }

  if (location.pathname === '/ui-preview/zero-onboarding') {
    return <ZeroOnboardingPreview />;
  }

  if (authState === 'loading') {
    return (
      <div data-testid="auth-loading" className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-400" data-testid="auth-loading-hint">{authHint || 'Validating session...'}</p>
        </div>
      </div>
    );
  }

  if (authState === 'unreachable') {
    return (
      <div data-testid="auth-unreachable" className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xl font-bold">!</div>
          <h2 className="text-base font-bold text-slate-800">Can't reach the server right now</h2>
          <p className="text-sm text-slate-500 mt-1">You're still signed in — this is usually a deployment or restart. Try again in a few seconds.</p>
          <button onClick={() => window.location.reload()} className="mt-4 h-9 px-4 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold" data-testid="auth-retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  if (authState === 'login' || !currentUser) {
    // Allow the public password-reset page even without a session.
    if (location.pathname === '/forgot-password') {
      return (
        <Suspense fallback={<RouteFallback />}>
          <ForgotPassword />
        </Suspense>
      );
    }
    return <LoginPage onLogin={handleLogin} />;
  }

  const title = pageTitles[location.pathname]
    || (location.pathname.startsWith('/travel-approvals/') ? 'Review Travel Plan'
      : location.pathname.startsWith('/approvals/') ? 'Review Approval'
      : 'Task Tracker');

  return (
    <div className="min-h-screen bg-neutral-50">
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        onProfileClick={() => setProfileOpen(true)}
      />
      <main className={`transition-all duration-300 ${isMobile ? 'ml-0' : collapsed ? 'ml-20' : 'ml-64'}`}>
        {!(isMobile && location.pathname === '/home') && (
          <TopBar title={title} onMenuClick={() => setMobileOpen(true)} isMobile={isMobile} onLogout={handleLogout} />
        )}
        <div className={`${isMobile ? 'px-4 pt-3 pb-24 max-w-full overflow-x-hidden' : 'px-8 pt-4 pb-8'}`}>
          <PushGate />
          <BranchUnmappedBanner />
          <DailyReverifyGate />
          <ExcelPasswordDialog />
          <SubUserGuard>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Navigate to={isMobile ? '/home' : '/task-tracker'} replace />} />
            <Route path="/dashboard" element={<Navigate to={isMobile ? '/home' : '/task-tracker'} replace />} />
            <Route path="/home" element={isMobile ? <MobileHome onProfile={() => setProfileOpen(true)} onLogout={handleLogout} /> : <Navigate to="/task-tracker" replace />} />
            <Route path="/team" element={<Suspense fallback={<RouteFallback />}><TeamManagement /></Suspense>} />
            <Route path="/visits" element={<Suspense fallback={<RouteFallback />}><Visits /></Suspense>} />
            <Route path="/places" element={<Suspense fallback={<RouteFallback />}><BusinessPlaces /></Suspense>} />
            <Route path="/sales-points" element={<Suspense fallback={<RouteFallback />}><SalesPoints /></Suspense>} />
            <Route path="/performance" element={<Suspense fallback={<RouteFallback />}><Performance /></Suspense>} />
            <Route path="/planner" element={<Suspense fallback={<RouteFallback />}><Visits /></Suspense>} />
            <Route path="/org-tree" element={<Suspense fallback={<RouteFallback />}><UserTree /></Suspense>} />
            <Route path="/approvals" element={<Suspense fallback={<RouteFallback />}><Approvals /></Suspense>} />
            <Route path="/approvals/:category/:itemId" element={<Suspense fallback={<RouteFallback />}><ApprovalReview /></Suspense>} />
            <Route path="/import" element={<Suspense fallback={<RouteFallback />}><DataImport /></Suspense>} />
            <Route path="/admin" element={<Suspense fallback={<RouteFallback />}><AdminPanel /></Suspense>} />
            <Route path="/admin/rewards-preview" element={<Suspense fallback={<RouteFallback />}><RewardsMockup /></Suspense>} />
            <Route path="/admin/lead-distribution-preview" element={<Suspense fallback={<RouteFallback />}><LeadDistributionMockup /></Suspense>} />
            <Route path="/travel-plans" element={<Suspense fallback={<RouteFallback />}><TravelPlan /></Suspense>} />
            <Route path="/recently-deleted" element={<Suspense fallback={<RouteFallback />}><RecentlyDeleted /></Suspense>} />
            <Route path="/attendance" element={<Suspense fallback={<RouteFallback />}><Attendance /></Suspense>} />
            <Route path="/field-activity" element={<Suspense fallback={<RouteFallback />}><FieldActivity /></Suspense>} />
            <Route path="/travel-approvals" element={<Suspense fallback={<RouteFallback />}><TravelApprovals /></Suspense>} />
            <Route path="/travel-approvals/:planId" element={<Suspense fallback={<RouteFallback />}><TravelApprovalReview /></Suspense>} />
            <Route path="/unplanned-visits" element={<Suspense fallback={<RouteFallback />}><UnplannedVisits /></Suspense>} />
            <Route path="/revenue" element={<Suspense fallback={<RouteFallback />}><Revenue /></Suspense>} />
            <Route path="/margin" element={<Suspense fallback={<RouteFallback />}><MarginManagement /></Suspense>} />
            <Route path="/hr-dashboard" element={<Suspense fallback={<RouteFallback />}><HRDashboard /></Suspense>} />
            <Route path="/payout" element={<Suspense fallback={<RouteFallback />}><PayoutManagement /></Suspense>} />
            <Route path="/leads" element={<Suspense fallback={<RouteFallback />}><Leads /></Suspense>} />
            <Route path="/call-tracker" element={<Suspense fallback={<RouteFallback />}><CallTracker /></Suspense>} />
            <Route path="/task-tracker" element={<Suspense fallback={<RouteFallback />}><TaskTracker /></Suspense>} />
            <Route path="/mobile-lookup" element={<Suspense fallback={<RouteFallback />}><MobileNumberLookup /></Suspense>} />
            <Route path="/idfc-toll-txn" element={<Suspense fallback={<RouteFallback />}><IdfcTollTxn /></Suspense>} />
            <Route path="/livquik-toll-txn" element={<Suspense fallback={<RouteFallback />}><LivquikTollTxn /></Suspense>} />
            <Route path="/third-party-logins" element={<Suspense fallback={<RouteFallback />}><ThirdPartyApps /></Suspense>} />
            <Route path="/chatbot-admin" element={<Suspense fallback={<RouteFallback />}><ChatbotAdmin /></Suspense>} />
            <Route path="/support-chat" element={<Suspense fallback={<RouteFallback />}><SupportChat /></Suspense>} />
            <Route path="/prepaid-card-dispatch" element={<Suspense fallback={<RouteFallback />}><PrepaidCardDispatch /></Suspense>} />
            <Route path="/ncmc-inventory" element={<Suspense fallback={<RouteFallback />}><NcmcInventory /></Suspense>} />
            <Route path="/users-created" element={<Suspense fallback={<RouteFallback />}><UsersCreated /></Suspense>} />
            <Route path="/hitchzone-compare" element={<Suspense fallback={<RouteFallback />}><HitchZoneCompare /></Suspense>} />
            <Route path="/txn-auditor" element={<Suspense fallback={<RouteFallback />}><TxnAuditor /></Suspense>} />
            <Route path="/hiteam-wallet-txns" element={<Suspense fallback={<RouteFallback />}><HiteamWalletTxns /></Suspense>} />
            <Route path="/wallet-audit" element={<Suspense fallback={<RouteFallback />}><WalletAudit /></Suspense>} />
            <Route path="/notes" element={<Suspense fallback={<RouteFallback />}><TaskNotes /></Suspense>} />
            <Route path="/sheet-import" element={<Suspense fallback={<RouteFallback />}><SheetImport /></Suspense>} />
            <Route path="/lead-distribution" element={<Suspense fallback={<RouteFallback />}><LeadDistribution /></Suspense>} />
            <Route path="/lead-tracking" element={<Suspense fallback={<RouteFallback />}><LeadTracking /></Suspense>} />
            <Route path="/perf-watch" element={<Suspense fallback={<RouteFallback />}><PerfWatch /></Suspense>} />
            <Route path="/reminders" element={<Suspense fallback={<RouteFallback />}><Reminders /></Suspense>} />
            <Route path="/sub-users" element={<Suspense fallback={<RouteFallback />}><SubUserManager /></Suspense>} />
            <Route path="/expense-claims" element={<Suspense fallback={<RouteFallback />}><ExpenseClaims /></Suspense>} />
            <Route path="/notifications" element={<Suspense fallback={<RouteFallback />}><HitchNotifications /></Suspense>} />
            <Route path="/email-notifications" element={<Suspense fallback={<RouteFallback />}><EmailNotifications /></Suspense>} />
            <Route path="/hitchzone-audit" element={<Suspense fallback={<RouteFallback />}><HitchZoneAudit /></Suspense>} />
            <Route path="/tally" element={<Suspense fallback={<RouteFallback />}><Tally /></Suspense>} />
            <Route path="/onboard-id-list" element={<Suspense fallback={<RouteFallback />}><OnboardIdList /></Suspense>} />
            <Route path="/branches" element={<Suspense fallback={<RouteFallback />}><BranchesPage /></Suspense>} />
            <Route path="/vehicle-inventory" element={<Suspense fallback={<RouteFallback />}><VehicleInventory /></Suspense>} />
            <Route path="/vehicle-bookings" element={<Suspense fallback={<RouteFallback />}><VehicleBookings /></Suspense>} />
            <Route path="/payments" element={<Suspense fallback={<RouteFallback />}><Payments /></Suspense>} />
            <Route path="/vehicle-model-master" element={<Suspense fallback={<RouteFallback />}><VehicleModelMaster /></Suspense>} />
            <Route path="/vehicle-pricing" element={<Suspense fallback={<RouteFallback />}><VehiclePricing /></Suspense>} />
            <Route path="/create-quote" element={<Suspense fallback={<RouteFallback />}><CreateQuote /></Suspense>} />
            <Route path="/billing" element={<Suspense fallback={<RouteFallback />}><BillingModule /></Suspense>} />
            <Route path="/vehicle-addition" element={<Suspense fallback={<RouteFallback />}><VehicleAddition /></Suspense>} />
            <Route path="/inventory" element={<Suspense fallback={<RouteFallback />}><InventoryTracer /></Suspense>} />
            <Route path="/banking" element={<Suspense fallback={<RouteFallback />}><BankingTransactions /></Suspense>} />
            <Route path="/vrn-tracer" element={<Suspense fallback={<RouteFallback />}><VrnTracer /></Suspense>} />
            <Route path="/push-notifications" element={<Suspense fallback={<RouteFallback />}><PushNotifications /></Suspense>} />
            <Route path="/profile" element={<Suspense fallback={<RouteFallback />}><MyProfile /></Suspense>} />
            <Route path="/docs" element={<Suspense fallback={<RouteFallback />}><ApiDocs /></Suspense>} />
            <Route path="/diagnostics" element={<Suspense fallback={<RouteFallback />}><SystemDiagnostics /></Suspense>} />
          </Routes>
          </ErrorBoundary>
          </SubUserGuard>
        </div>
      </main>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
      {isMobile && <MobileTabBar uid={currentUser?.id} onProfile={() => setProfileOpen(true)} />}
      {isMobile && <MobilePrimaryFab />}
      <ScrollHint />
      <HiteamSetupPrompt open={showHiteamSetup} onOpenChange={(v) => {
        setShowHiteamSetup(v);
        if (!v) localStorage.setItem('hiteam_setup_dismissed', 'true');
      }} isMandatory={false} />
      <PasswordSetupPrompt open={showPasswordSetup} onOpenChange={(v) => {
        setShowPasswordSetup(v);
        if (!v) localStorage.setItem('password_setup_dismissed', 'true');
      }} isMandatory={false} />
      <BulkPushDock />
      <ExternalApiDock />
      <BearerRefreshMonitor />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <AppContent />
        <GlobalBackfillIndicator />
        <DeploymentBanner />
        <Toaster position="top-right" richColors />
      </AppProvider>
    </BrowserRouter>
  );
}

export default App;
